import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import GalleryContext from '@/src/Gallery/components/GalleryContext';
import useQueryString from '@/src/Common/components/hooks/useQueryString';
import DownloadButtonV2 from '@/src/Share/components/DownloadButtonV2';
import React from 'react';
import useGalleryBehaviors from '@/src/Gallery/components/hooks/useGalleryBehvaiors';
import GalleryLightbox from '@/src/Gallery/components/GalleryLightbox';
import {
    useSelectionContainer,
    Box,
    boxesIntersect,
} from '@air/react-drag-to-select';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import ReplaceMedia from '@/src/Common/components/ReplaceMedia';
import { isHotkeyPressed, useHotkeys } from 'react-hotkeys-hook';
import moment from 'moment-timezone';

// Distance from the top/bottom of the viewport (in px) that triggers the
// drag-select auto-scroll. The closer the cursor gets to the edge, the
// faster the page scrolls, up to MAX_AUTO_SCROLL_SPEED_PX.
const AUTO_SCROLL_EDGE_ZONE_PX = 90;
const MAX_AUTO_SCROLL_SPEED_PX = 26;

interface ActivationDevice {
    id: number;
    activationId: number;
    deviceKeyId: string;
    friendlyName: string | null;
    deviceName: string | null;
    lastUploadAt: string | null;
    displayName: string;
}

interface PhotoManagerListProps {
    photos: MediaInstance[];
    setSelectedShareIdentifiers: (shareIdentifiers: string[]) => void;
    selectedShareIdentifiers: string[];
    columnCount: number;
    baseUrl: string | null;
    slug: string | null;
    activationDevices: ActivationDevice[];
}

let columnHeights: number[] = [];

export default function PhotoManagerList({
    photos,
    setSelectedShareIdentifiers,
    selectedShareIdentifiers,
    columnCount,
    baseUrl,
    slug,
    activationDevices,
}: PhotoManagerListProps) {
    // Helper to get device display name
    const getDeviceDisplayName = useCallback((deviceKeyId: string | null): string | null => {
        if (!deviceKeyId) return null;
        const device = activationDevices.find(d => d.deviceKeyId === deviceKeyId);
        return device?.displayName || device?.deviceName || null;
    }, [activationDevices]);
    const [columns, setColumns] = useState<number>(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const GAP = 0; // Gap between images in pixels
    const [columnWidth, setColumnWidth] = useState<number>(0);
    const query = useQueryString();
    const urlShareIdentifier = query.get('sid');
    const [shareIdentifierToIndexLookup, setShareIdentifierToIndexLookup] =
        useState<{ [key: string]: number }>({});

    // Calculate number of columns based on container width
    useEffect(() => {
        const updateColumns = () => {
            const width = containerRef.current?.offsetWidth ?? 0;
            /*if (width < 1200) {
                setColumns(2);
                setColumnWidth(width / 2);
            } else if (width < 1800) {
                setColumns(3);
                setColumnWidth(width / 3);
            } else {
                setColumns(4);
                setColumnWidth(width / 4);
            }*/
            setColumns(columnCount);
            setColumnWidth(width / columnCount);
        };

        updateColumns();
        window.addEventListener('resize', updateColumns);
        return () => window.removeEventListener('resize', updateColumns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnCount]);

    // Calculate positions for each photo
    const getPhotoPositions = useMemo(() => {
        columnHeights = new Array(columns).fill(0);
        return photos.map((photo, index) => {
            // @ts-ignore
            const aspectRatio =
                // @ts-ignore
                photo.aspectRatio || 1.5;
            const width = `${columnWidth}px`;
            const shortestColumn = columnHeights.indexOf(
                Math.min(...columnHeights),
            );
            const x = `${
                shortestColumn * (100 / columns) + shortestColumn * GAP
            }%`;
            const y = columnHeights[shortestColumn];

            const photoHeight = columnWidth / aspectRatio;
            columnHeights[shortestColumn] += photoHeight + GAP;

            return {
                id: photo.id,
                style: {
                    position: 'absolute',
                    left: x,
                    top: y,
                    width,
                    height: `${photoHeight}px`,
                    display: 'flex',
                },
                shareIdentifier: photo.shareIdentifier,
                thumbnail: photo.thumbnail_800pxUri,
                full: photo.originalUri,
                tiny: photo.thumbnailTinyUri,
                isHidden: photo.isHidden,
                isFavorite: photo.isFavorite,
                mimeType: photo.mimeType,
                capturedAt: photo.createdAt,
                photoSourceName: getDeviceDisplayName(photo.deviceKeyId),
                tags: photo.tags ?? [],
                imageStyle: {
                    objectFit: 'contain',
                    backgroundSize: 'cover',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    transition: 'all 0.3s linear',
                    margin: 4,
                    flex: 1,
                },
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos, columns, columnWidth, columnCount, getDeviceDisplayName]);

    const [shareIdentifier, setShareIdentifier] = useState<string | null>(
        urlShareIdentifier ?? null,
    );
    const [photoIndex, setPhotoIndex] = useState<number>(0);
    /*const [searchParams, setSearchParams] = useSearchParams();
    const currentSearchParams = Object.fromEntries(
        Array.from(searchParams.entries()).filter(
            ([key]: [string, string]) => key !== 'sid',
        ),
    );*/

    useEffect(() => {
        let lookup: { [key: string]: number } = {};
        photos.forEach((photo, index) => {
            lookup[photo.shareIdentifier] = index;
        });

        setShareIdentifierToIndexLookup(lookup);
    }, [photos]);

    useEffect(() => {
        if (shareIdentifier) {
            setPhotoIndex(shareIdentifierToIndexLookup[shareIdentifier] ?? 0);

            //      setSearchParams({ ...currentSearchParams, sid: shareIdentifier });

            const element = document.getElementById(
                `${shareIdentifier}-element`,
            );
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            /*     setSearchParams({
                ...currentSearchParams,
            });*/
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        shareIdentifierToIndexLookup,
        shareIdentifier,
        photos,
        setPhotoIndex,
        /*setSearchParams,
        searchParams,*/
    ]);

    const { swipeHandlers } = useGalleryBehaviors(
        () => {
            setShareIdentifier(null);
        },
        () => {
            if (!shareIdentifier) {
                return;
            }

            let index = shareIdentifierToIndexLookup[shareIdentifier] ?? 0;

            if (index >= 0 && photos[index + 1]) {
                setShareIdentifier(photos[index + 1].shareIdentifier);
            } else {
                setShareIdentifier(null);
            }
        },
        () => {
            if (!shareIdentifier) {
                return;
            }

            let index = shareIdentifierToIndexLookup[shareIdentifier] ?? 0;

            if (index >= 0 && photos[index - 1]) {
                setShareIdentifier(photos[index - 1].shareIdentifier);
            } else {
                setShareIdentifier(null);
            }
        },
    );

    return (
        <GalleryContext.Provider
            value={{
                shareIdentifier,
                setShareIdentifier,
                baseUrl: baseUrl,
                slug: slug,
            }}
        >
            <div
                ref={containerRef}
                className="relative w-full"
                style={{ height: `${Math.max(...columnHeights)}px` }}
            >
                {/*
                 * Defer rendering the photo grid until the container has been
                 * measured (columnWidth > 0). On the first paint columnWidth
                 * is 0, which would collapse every PhotoPreview to a 0x0 rect
                 * at (0,0); the IntersectionObserver in useIsVisible would
                 * then fire isIntersecting: true for every photo at once,
                 * latch hasLoaded.current = true on each, and force the
                 * entire activation to load even when scrolled offscreen.
                 * Holding the children back for one tick lets the dimensions
                 * effect run first.
                 */}
                {columnWidth > 0 && (
                    <GalleryLayout
                        key={columnCount}
                        layout={getPhotoPositions}
                        setShareIdentifier={setShareIdentifier}
                        setSelectedShareIdentifiers={setSelectedShareIdentifiers}
                        containerRef={containerRef}
                        selectedShareIdentifiers={selectedShareIdentifiers}
                    />
                )}
            </div>
            <GalleryLightbox
                photos={photos}
                currentPhotoIndex={photoIndex}
                gestureHandlers={swipeHandlers}
                showLabels={true}
                showFaces={true}
                showHideButton={true}
                showTags={true}
            />
        </GalleryContext.Provider>
    );
}

const GalleryLayout = React.memo(function GalleryLayout({
    layout,
    setShareIdentifier,
    setSelectedShareIdentifiers,
    selectedShareIdentifiers,
    containerRef,
}: {
    layout: any[];
    setShareIdentifier: (shareIdentifier: string) => void;
    setSelectedShareIdentifiers: (shareIdentifiers: string[]) => void;
    selectedShareIdentifiers: string[];
    containerRef: React.RefObject<HTMLDivElement>;
}) {
    // Keyed by shareIdentifier so reorders, filters, and async photo arrivals
    // don't desync the hit-test from the rendered grid. Stored in document
    // coordinates (viewport rect + window scroll at measurement time) so they
    // remain valid regardless of where the user has scrolled.
    const selectableItems = useRef<Map<string, Box>>(new Map());

    // Latest props captured in refs so the long-lived selection callbacks
    // (registered once inside useSelectionContainer) always see fresh values
    // without re-subscribing on every render.
    const layoutRef = useRef(layout);
    const selectedShareIdentifiersRef = useRef(selectedShareIdentifiers);
    layoutRef.current = layout;
    selectedShareIdentifiersRef.current = selectedShareIdentifiers;

    const selectedSet = useMemo(
        () => new Set(selectedShareIdentifiers),
        [selectedShareIdentifiers],
    );

    useHotkeys('mod+a', (e) => {
        e.preventDefault();
        setSelectedShareIdentifiers(
            layoutRef.current.map((item) => item.shareIdentifier),
        );
    });

    const remeasureSelectableItems = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        const next = new Map<string, Box>();
        const nodes = container.querySelectorAll<HTMLElement>(
            '[data-share-identifier]',
        );
        nodes.forEach((node) => {
            const sid = node.dataset.shareIdentifier;
            if (!sid) return;
            const rect = node.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;
            next.set(sid, {
                left: rect.left + scrollX,
                top: rect.top + scrollY,
                width: rect.width,
                height: rect.height,
            });
        });
        selectableItems.current = next;
    }, [containerRef]);

    // Re-measure whenever the layout changes (filter, sort, column resize,
    // new photos arriving). We wait one frame so React has committed the new
    // styles, then a short timeout to outlast the CSS position transitions.
    useLayoutEffect(() => {
        let cancelled = false;
        const raf = requestAnimationFrame(() => {
            if (!cancelled) remeasureSelectableItems();
        });
        const t = window.setTimeout(() => {
            if (!cancelled) remeasureSelectableItems();
        }, 350);
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
            window.clearTimeout(t);
        };
    }, [layout, remeasureSelectableItems]);

    useEffect(() => {
        const onResize = () => remeasureSelectableItems();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [remeasureSelectableItems]);

    // Edge auto-scroll: while a drag selection is active, scroll the page when
    // the cursor enters the top or bottom edge zone. Speed scales with how
    // deep the cursor is into the zone. Runs on rAF so it stays smooth and
    // stops cleanly when the drag ends or the cursor moves away from the edge.
    const isDragSelectingRef = useRef(false);
    const pointerYRef = useRef(0);
    const scrollFrameRef = useRef<number | null>(null);
    const lastFrameTimeRef = useRef<number>(0);

    const stopAutoScrollLoop = useCallback(() => {
        if (scrollFrameRef.current != null) {
            cancelAnimationFrame(scrollFrameRef.current);
            scrollFrameRef.current = null;
        }
        lastFrameTimeRef.current = 0;
    }, []);

    const startAutoScrollLoop = useCallback(() => {
        if (scrollFrameRef.current != null) return;
        const tick = (now: number) => {
            if (!isDragSelectingRef.current) {
                scrollFrameRef.current = null;
                lastFrameTimeRef.current = 0;
                return;
            }
            const prev = lastFrameTimeRef.current || now;
            // Normalize by frame time so scroll feels the same regardless of
            // refresh rate (clamped to avoid huge jumps if a frame is dropped).
            const dtScale = Math.min((now - prev) / 16.6667, 3);
            lastFrameTimeRef.current = now;

            const y = pointerYRef.current;
            const vh = window.innerHeight;
            let delta = 0;
            if (y < AUTO_SCROLL_EDGE_ZONE_PX) {
                const intensity = Math.min(
                    1,
                    (AUTO_SCROLL_EDGE_ZONE_PX - y) / AUTO_SCROLL_EDGE_ZONE_PX,
                );
                delta = -intensity * MAX_AUTO_SCROLL_SPEED_PX * dtScale;
            } else if (y > vh - AUTO_SCROLL_EDGE_ZONE_PX) {
                const intensity = Math.min(
                    1,
                    (y - (vh - AUTO_SCROLL_EDGE_ZONE_PX)) /
                        AUTO_SCROLL_EDGE_ZONE_PX,
                );
                delta = intensity * MAX_AUTO_SCROLL_SPEED_PX * dtScale;
            }
            if (delta !== 0) {
                window.scrollBy(0, delta);
            }
            scrollFrameRef.current = requestAnimationFrame(tick);
        };
        scrollFrameRef.current = requestAnimationFrame(tick);
    }, []);

    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            pointerYRef.current = e.clientY;
            if (isDragSelectingRef.current) {
                const vh = window.innerHeight;
                const inEdgeZone =
                    e.clientY < AUTO_SCROLL_EDGE_ZONE_PX ||
                    e.clientY > vh - AUTO_SCROLL_EDGE_ZONE_PX;
                if (inEdgeZone) {
                    startAutoScrollLoop();
                }
            }
        };
        window.addEventListener('mousemove', handleMove);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            stopAutoScrollLoop();
        };
    }, [startAutoScrollLoop, stopAutoScrollLoop]);

    const { DragSelection } = useSelectionContainer({
        eventsElement: document.getElementById('root'),
        onSelectionChange: (box) => {
            const scrollAwareBox: Box = {
                ...box,
                top: box.top + window.scrollY,
                left: box.left + window.scrollX,
            };

            const selectedSids: string[] = [];
            // Preserve layout order so downstream consumers (toolbar actions,
            // zip exports, etc.) get a deterministic list.
            layoutRef.current.forEach((item) => {
                const rect = selectableItems.current.get(item.shareIdentifier);
                if (rect && boxesIntersect(scrollAwareBox, rect)) {
                    selectedSids.push(item.shareIdentifier);
                }
            });

            const prev = selectedShareIdentifiersRef.current;
            // Skip the state update when nothing changed to avoid re-rendering
            // every PhotoCard on every mousemove tick.
            if (
                selectedSids.length !== prev.length ||
                selectedSids.some((sid, i) => sid !== prev[i])
            ) {
                setSelectedShareIdentifiers(selectedSids);
            }
        },
        onSelectionStart: () => {
            isDragSelectingRef.current = true;
            // Measure immediately so the very first onSelectionChange tick
            // hit-tests against the current grid, not stale rects.
            remeasureSelectableItems();
        },
        onSelectionEnd: () => {
            isDragSelectingRef.current = false;
            stopAutoScrollLoop();
        },
        selectionProps: {
            style: {
                border: '1px solid rgba(37, 99, 235, 0.9)',
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                zIndex: 1000,
            },
        },
        isEnabled: true,
    });

    const handleCardClick = useCallback(
        (shareIdentifier: string, e: React.MouseEvent) => {
            const isAdditive = isHotkeyPressed('shift');
            if (!isAdditive) {
                setSelectedShareIdentifiers([shareIdentifier]);
                return;
            }
            e.stopPropagation();
            const current = selectedShareIdentifiersRef.current;
            if (current.includes(shareIdentifier)) {
                setSelectedShareIdentifiers(
                    current.filter((id) => id !== shareIdentifier),
                );
            } else {
                setSelectedShareIdentifiers([...current, shareIdentifier]);
            }
        },
        [setSelectedShareIdentifiers],
    );

    return (
        <>
            <DragSelection />
            {layout.map((item) => (
                <PhotoCard
                    key={`${item.shareIdentifier}-gutter`}
                    item={item}
                    selected={selectedSet.has(item.shareIdentifier)}
                    onSelect={handleCardClick}
                    setShareIdentifier={setShareIdentifier}
                />
            ))}
        </>
    );
});

interface PhotoCardProps {
    item: any;
    selected: boolean;
    onSelect: (shareIdentifier: string, e: React.MouseEvent) => void;
    setShareIdentifier: (shareIdentifier: string) => void;
}

const PhotoCard = React.memo(function PhotoCard({
    item,
    selected,
    onSelect,
    setShareIdentifier,
}: PhotoCardProps) {
    const {
        id,
        style,
        imageStyle,
        thumbnail,
        full,
        tiny,
        shareIdentifier,
        mimeType,
        isHidden,
        isFavorite,
        capturedAt,
        photoSourceName,
        tags,
    } = item;
    const handleClick = useCallback(
        (e: React.MouseEvent) => onSelect(shareIdentifier, e),
        [onSelect, shareIdentifier],
    );
    return (
        // @ts-ignore
        <div
            data-share-identifier={shareIdentifier}
            // @ts-ignore
            style={{
                ...(style ?? {}),
                border: selected
                    ? '1px solid rgba(37, 99, 235, 0.9)'
                    : '1px solid transparent',
                // Animate only the highlight, not position/size. Animating
                // position would cause getBoundingClientRect() to return
                // intermediate values during reflows and corrupt the
                // drag-select hit-test.
                transition: 'border-color 0.15s ease',
            }}
            onClick={handleClick}
        >
            <PhotoPreview
                full={full}
                thumbnail={thumbnail || full}
                tiny={tiny}
                style={imageStyle ?? {}}
                shareIdentifier={shareIdentifier}
                id={id}
                mimeType={mimeType}
                setShareIdentifier={setShareIdentifier}
                isHidden={isHidden}
                isFavorite={isFavorite}
                capturedAt={capturedAt}
                photoSourceName={photoSourceName}
                tags={tags}
            />
        </div>
    );
});

function PhotoPreview({
    style,
    thumbnail,
    tiny,
    shareIdentifier,
    id,
    mimeType,
    full,
    setShareIdentifier,
    isHidden,
    isFavorite,
    capturedAt,
    photoSourceName,
    tags,
}: {
    style: any;
    thumbnail: string;
    tiny: string;
    shareIdentifier: string;
    id: number;
    mimeType: string;
    full: string;
    isHidden: boolean;
    isFavorite: boolean;
    setShareIdentifier: (shareIdentifier: string) => void;
    capturedAt: string | null;
    photoSourceName: string | null;
    tags: string[];
}) {
    const [uri, setUri] = useState<string>(tiny || thumbnail);
    const [, setIsLoading] = useState<boolean>(true);
    const ref = useRef<HTMLDivElement>(null);
    const visibility = useIsVisible(ref);
    const hasLoaded = useRef<boolean>(false);
    const [isFavoriting, setIsFavoriting] = useState<boolean>(false);
    const [isReplacing, setIsReplacing] = useState<boolean>(false);
    const [isHiding, setIsHiding] = useState<boolean>(false);
    const [localIsHidden, setLocalIsHidden] = useState<boolean>(isHidden);
    const [localIsFavorite, setLocalIsFavorite] = useState<boolean>(isFavorite);

    const authenticatedApi = useAuthenticatedApi();
    const toggleHidden = async (
        mediaId: number,
        isHidden: boolean,
    ): Promise<boolean> => {
        const toggleVisibility = async () => {
            await authenticatedApi.put(`media/${mediaId}/visibility`, {
                isHidden: !isHidden,
            });

            return !isHidden;
        };

        return await toggleVisibility();
    };

    const toggleFavorite = async (
        mediaId: number,
        isFavorite: boolean,
    ): Promise<boolean> => {
        const toggle = async () => {
            await authenticatedApi.put(`media/${mediaId}/favorite`, {
                isFavorite: !isFavorite,
            });

            return !isFavorite;
        };

        return await toggle();
    };

    useEffect(() => {
        if (visibility) {
            const image = new Image();
            image.onload = () => {
                setUri(thumbnail);
                setIsLoading(false);
            };
            image.src = thumbnail;

            return () => {
                // Bail on the in-flight thumbnail download. Setting src=''
                // aborts the fetch in modern browsers; nulling the handlers
                // guards against a late completion racing into setUri after
                // we've scrolled away or unmounted.
                image.onload = null;
                image.onerror = null;
                image.src = '';
            };
        } else {
            setUri(tiny || thumbnail);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thumbnail, visibility]);

    useEffect(() => {
        if (visibility) {
            hasLoaded.current = true;
        }
    }, [visibility]);

    const handleExpandClick = useCallback(
        (e: any) => {
            setShareIdentifier(shareIdentifier);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shareIdentifier],
    );

    if (!visibility && !hasLoaded.current) {
        return (
            <div
                ref={ref}
                key={shareIdentifier}
                style={{
                    ...style,
                    position: 'relative',
                    transition: 'all 0.3s linear',
                }}
            ></div>
        );
    }

    const capturedAtText = capturedAt
        ? moment(capturedAt).format('MMM D, YYYY h:mm a')
        : '';

    return (
        <div
            ref={ref}
            key={shareIdentifier}
            style={{
                ...style,
                position: 'relative',
                backgroundImage:
                    visibility || hasLoaded.current ? `url(${uri})` : 'none',
                opacity: localIsHidden ? 0.5 : 1,
                transition: 'all 0.3s linear',
            }}
            className="bg-no-repeat group"
            id={`${shareIdentifier}-element`}
        >
            <>
                {mimeType === 'video/mp4' && (
                    <div className="button-container">
                        <i
                            className="bi bi-play"
                            style={{ filter: 'drop-shadow(0px 0px 5px #222)' }}
                        />
                    </div>
                )}
                {(photoSourceName || tags.length > 0) && (
                    <div className="absolute top-1 left-1 flex flex-col items-start gap-1 max-w-[75%] text-white drop-shadow-md">
                        {photoSourceName && (
                            <div className="text-xs bg-black/40 px-1.5 py-0.5 rounded">{photoSourceName}</div>
                        )}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {tags.slice(0, 3).map((tag) => (
                                    <span
                                        key={tag}
                                        className="text-[10px] bg-black/40 px-1.5 py-0.5 rounded-full truncate max-w-[110px]"
                                    >
                                        {tag}
                                    </span>
                                ))}
                                {tags.length > 3 && (
                                    <span className="text-[10px] bg-black/40 px-1.5 py-0.5 rounded-full">
                                        +{tags.length - 3}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {capturedAtText && (
                    <div className="absolute top-1 right-1 text-white drop-shadow-md">
                        <div className="text-xs">{capturedAtText}</div>
                    </div>
                )}
                <div className="absolute bottom-0  right-0 left-0 bg-gradient-to-t from-[rgba(0,0,0,0.5)] to-transparent opacity-80 h-[60px] visible md:invisible group-hover:invisible md:group-hover:visible ">
                    <div className="flex justify-start items-end h-full gap-0 flex-row text-white px-4 pb-4">
                        <div
                            onClick={async (event: any) => {
                                setTimeout(() => setIsFavoriting(true), 0);
                                event.stopPropagation();
                                event.preventDefault();

                                const newState = await toggleFavorite(
                                    id,
                                    localIsFavorite,
                                );
                                setLocalIsFavorite(newState);
                                setTimeout(() => setIsFavoriting(false), 0);
                            }}
                        >
                            <i
                                className={`bi ${
                                    isFavoriting
                                        ? 'bi-hourglass'
                                        : !localIsFavorite
                                        ? 'bi-heart'
                                        : 'bi-heart-fill'
                                } clickable`}
                            />
                        </div>
                        &nbsp; &nbsp;
                        <div
                            onClick={async (event: any) => {
                                event.stopPropagation();
                                event.preventDefault();

                                setIsReplacing(true);
                            }}
                        >
                            <i className={`bi bi-pencil clickable`} />
                        </div>
                        &nbsp; &nbsp;
                        <div
                            onClick={async (event: any) => {
                                setTimeout(() => setIsHiding(true), 0);
                                event.stopPropagation();
                                event.preventDefault();

                                const newState = await toggleHidden(
                                    id,
                                    isHidden,
                                );

                                setLocalIsHidden(newState);

                                setTimeout(() => setIsHiding(false), 0);
                            }}
                        >
                            <i
                                className={`bi ${
                                    isHiding
                                        ? 'bi-hourglass'
                                        : !localIsHidden
                                        ? 'bi-eye'
                                        : 'bi-eye-slash'
                                } clickable`}
                            />
                        </div>
                        &nbsp; &nbsp;
                        <DownloadButtonV2
                            uri={full}
                            mimeType={mimeType}
                            mediaId={id}
                            source="gallery"
                        />
                        <div className="spacer" />
                        <div onClick={handleExpandClick}>
                            <i className="bi bi-fullscreen clickable" />
                        </div>
                    </div>
                </div>
            </>
            {isReplacing && (
                <ReplaceMedia
                    mediaId={id}
                    onComplete={(uri: string) => {
                        setUri(uri);

                        setIsReplacing(false);
                    }}
                    onCancel={() => {
                        setIsReplacing(false);
                    }}
                />
            )}
        </div>
    );
}

export function useIsVisible(ref: React.RefObject<HTMLDivElement>) {
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Defense-in-depth: if the target has zero area, the
                // intersection rect is meaningless and any
                // isIntersecting=true reading is spurious (e.g. during a
                // pre-layout window before width/height have been computed).
                // Drop the callback rather than letting it latch hasLoaded
                // downstream.
                const { width, height } = entry.boundingClientRect;
                if (width === 0 || height === 0) {
                    return;
                }
                setIntersecting(entry.isIntersecting);
            },
            {
                rootMargin: '300px 0px 600px 0px',
            },
        );

        if (ref.current) {
            observer.observe(ref.current);
        }
        return () => {
            observer.disconnect();
        };
    }, [ref]);

    return isIntersecting;
}
